import { ReactNode, useCallback, useMemo, useState } from 'react';
import { ComponentWithAs, forwardRef } from '@chakra-ui/react';
import { runIfFn } from '@chakra-ui/utils';

import { Params } from 'common/hooks/useAuthenticatedAction/types';
import DetailSidebar, { DetailSidebarProps } from 'components/DetailSidebar';
import DetailSidebarBody from 'components/DetailSidebar/Body';
import DetailSidebarFooter from 'components/DetailSidebar/Footer';
import DetailSidebarHeader from 'components/DetailSidebar/Header';

import PasscodeDetailSidebar from './components/PasscodeDetailSidebar';

type RenderPropsApi = {
  showPasscode: () => void;
  hidePasscode: () => void;
};

type RenderProps = ReactNode | ((api: RenderPropsApi) => ReactNode);

type StrongAuthDetailSidebarProps = Omit<DetailSidebarProps, 'children'> &
  Pick<Params, 'scope' | 'onPasscodeSuccess'> & {
    children: RenderProps;
    description?: string;
  };

type SubComponents = {
  Header: typeof DetailSidebarHeader;
  Body: typeof DetailSidebarBody;
  Footer: typeof DetailSidebarFooter;
};

export const StrongAuthDetailSidebar = forwardRef<
  StrongAuthDetailSidebarProps,
  'aside'
>(({ children, description, onPasscodeSuccess, scope, ...props }, ref) => {
  const [isPasscodeShown, setShowPasscode] = useState(false);

  const showPasscode = useCallback(() => {
    setShowPasscode(true);
  }, [setShowPasscode]);
  const hidePasscode = useCallback(() => {
    setShowPasscode(false);
  }, [setShowPasscode]);

  const renderProps = useMemo(
    () => ({
      hidePasscode,
      showPasscode,
    }),
    [showPasscode, hidePasscode],
  );

  if (isPasscodeShown) {
    return (
      <PasscodeDetailSidebar
        description={description}
        onPasscodeSuccess={onPasscodeSuccess}
        scope={scope}
        {...props}
      />
    );
  }

  return (
    <DetailSidebar ref={ref} {...props}>
      {runIfFn(children, renderProps)}
    </DetailSidebar>
  );
}) as ComponentWithAs<'aside', StrongAuthDetailSidebarProps> & SubComponents;

StrongAuthDetailSidebar.displayName = 'StrongAuthDetailSidebar';

StrongAuthDetailSidebar.Header = DetailSidebarHeader;
StrongAuthDetailSidebar.Body = DetailSidebarBody;
StrongAuthDetailSidebar.Footer = DetailSidebarFooter;
